import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Save, Send, Eye, EyeOff, Plus, Trash2, History, GripVertical } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const STATUS_STYLE = {
  draft: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
  published: "bg-[#D1FAE5] text-[#065F46] border-[#10B98155]",
  archived: "bg-[#FEE2E2] text-[#991B1B] border-[#EF444455]",
};

export default function MobileAppPageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/mobile-app-pages/admin/${id}`);
      setPage(r.data);
      setDraft({
        title_en: r.data.title_en || "",
        title_ar: r.data.title_ar || "",
        body_en: r.data.body_en || "",
        body_ar: r.data.body_ar || "",
        consent_items: r.data.consent_items || [],
        required_at_signup: !!r.data.required_at_signup,
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    setSaving(true);
    try {
      await api.patch(`/mobile-app-pages/admin/${id}`, draft);
      toast.success("Draft saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function handlePublish() {
    setPublishing(true);
    try {
      // Save any pending edits first, THEN publish, so what the mobile app
      // serves matches what the editor showed at click time.
      await api.patch(`/mobile-app-pages/admin/${id}`, draft);
      const r = await api.post(`/mobile-app-pages/admin/${id}/publish`);
      toast.success(`Published as v${r.data.version}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPublishing(false); }
  }

  async function openVersions() {
    try {
      const r = await api.get(`/mobile-app-pages/admin/${id}/versions`);
      setVersions(r.data.items || []);
      setVersionsOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function updateConsentItem(idx, patch) {
    const list = [...(draft.consent_items || [])];
    list[idx] = { ...list[idx], ...patch };
    setDraft({ ...draft, consent_items: list });
  }
  function addConsentItem() {
    setDraft({
      ...draft,
      consent_items: [
        ...(draft.consent_items || []),
        { id: `ci-${Date.now()}`, label_en: "", label_ar: "", required: true,
          order: (draft.consent_items || []).length + 1 },
      ],
    });
  }
  function removeConsentItem(idx) {
    const list = [...(draft.consent_items || [])];
    list.splice(idx, 1);
    setDraft({ ...draft, consent_items: list });
  }

  if (loading || !page || !draft) {
    return <div className="hah-card p-8 text-center text-[#6B7280]">Loading page…</div>;
  }

  const isConsent = page.page_type === "consent";

  return (
    <div className="space-y-4" data-testid="mobile-app-page-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate("/forms/mobile-app")}
                className="border-[#E5E7EB]">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Mobile App Forms
        </Button>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase font-bold ${STATUS_STYLE[page.status]}`}>
            {page.status} · v{page.version || 0}
          </span>
          <Button variant="outline" size="sm" onClick={openVersions} data-testid="mobile-editor-versions">
            <History className="w-4 h-4 mr-1.5" /> History
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPreview(!preview)}
                  data-testid="mobile-editor-preview-toggle">
            {preview ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
            {preview ? "Edit" : "Preview"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}
                  data-testid="mobile-editor-save-draft">
            <Save className="w-4 h-4 mr-1.5" /> Save draft
          </Button>
          <Button size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white"
                  onClick={handlePublish} disabled={publishing} data-testid="mobile-editor-publish">
            <Send className="w-4 h-4 mr-1.5" /> Publish
          </Button>
        </div>
      </div>

      <PageHeader
        title={page.title_en || page.slug}
        subtitle={`Slug: /${page.slug} · ${isConsent ? "Consent form (structured)" : "Markdown page"}${page.is_preset ? " · Preset" : ""}`}
      />

      {/* Metadata */}
      <div className="hah-card p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-[#374151] block mb-1">Title (English)</label>
          <Input value={draft.title_en} onChange={(e) => setDraft({ ...draft, title_en: e.target.value })}
                 data-testid="mobile-editor-title-en" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#374151] block mb-1">Title (Arabic)</label>
          <Input value={draft.title_ar} onChange={(e) => setDraft({ ...draft, title_ar: e.target.value })}
                 data-testid="mobile-editor-title-ar" dir="rtl" />
        </div>
        {isConsent && (
          <div className="md:col-span-2 flex items-center gap-3 pt-2 border-t border-[#E5E7EB]">
            <Switch checked={draft.required_at_signup}
                    onCheckedChange={(v) => setDraft({ ...draft, required_at_signup: v })}
                    data-testid="mobile-editor-required-toggle" />
            <div>
              <div className="text-sm font-semibold text-[#111827]">Required at signup</div>
              <div className="text-xs text-[#6B7280]">
                When enabled, the mobile app blocks onboarding until every required item is checked.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Body editors — bilingual side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BilingualBody
          lang="en" dir="ltr" title="English body"
          value={draft.body_en}
          onChange={(v) => setDraft({ ...draft, body_en: v })}
          preview={preview}
          testId="mobile-editor-body-en"
        />
        <BilingualBody
          lang="ar" dir="rtl" title="Arabic body"
          value={draft.body_ar}
          onChange={(v) => setDraft({ ...draft, body_ar: v })}
          preview={preview}
          testId="mobile-editor-body-ar"
        />
      </div>

      {/* Consent items builder */}
      {isConsent && (
        <div className="hah-card p-4" data-testid="mobile-editor-consent-items">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-bold text-[#111827]">Consent items</div>
              <div className="text-xs text-[#6B7280]">Each item becomes a checkbox in the mobile app. Required items must be ticked to proceed.</div>
            </div>
            <Button size="sm" variant="outline" onClick={addConsentItem} data-testid="mobile-editor-add-consent-item">
              <Plus className="w-4 h-4 mr-1" /> Add item
            </Button>
          </div>
          <div className="space-y-2">
            {(draft.consent_items || []).map((ci, idx) => (
              <div key={ci.id || idx} className="rounded-lg border border-[#E5E7EB] p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-start"
                   data-testid={`mobile-editor-consent-item-${idx}`}>
                <GripVertical className="w-4 h-4 text-[#9CA3AF] mt-2 hidden md:block col-span-1" />
                <div className="md:col-span-5">
                  <label className="text-[11px] font-semibold text-[#374151] block mb-1">Label (EN)</label>
                  <Textarea rows={2} value={ci.label_en}
                            onChange={(e) => updateConsentItem(idx, { label_en: e.target.value })} />
                </div>
                <div className="md:col-span-5">
                  <label className="text-[11px] font-semibold text-[#374151] block mb-1">Label (AR)</label>
                  <Textarea rows={2} value={ci.label_ar} dir="rtl"
                            onChange={(e) => updateConsentItem(idx, { label_ar: e.target.value })} />
                </div>
                <div className="md:col-span-1 flex flex-col items-center gap-2">
                  <Switch checked={ci.required}
                          onCheckedChange={(v) => updateConsentItem(idx, { required: v })}
                          data-testid={`consent-item-required-${idx}`} />
                  <span className="text-[10px] text-[#6B7280]">Required</span>
                  <Button variant="ghost" size="sm" className="text-[#EF4444] hover:bg-[#FEF2F2] h-7 px-2"
                          onClick={() => removeConsentItem(idx)}
                          data-testid={`consent-item-remove-${idx}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {(draft.consent_items || []).length === 0 && (
              <div className="text-sm text-[#6B7280] py-6 text-center">
                No consent items yet — add one to get started.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Version history dialog */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto" data-testid="mobile-editor-versions-dialog">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {versions.length === 0 && (
              <div className="text-sm text-[#6B7280] text-center py-8">
                No previous published versions yet.
              </div>
            )}
            {versions.map((v) => (
              <div key={v.id} className="rounded border border-[#E5E7EB] p-3">
                <div className="text-sm font-semibold">Version {v.version}</div>
                <div className="text-xs text-[#6B7280]">
                  Archived {v.archived_at?.slice(0, 19).replace("T", " ")}
                </div>
                <details className="mt-2">
                  <summary className="text-xs text-[#0B7E8C] cursor-pointer">View snapshot</summary>
                  <pre className="text-[11px] mt-2 max-h-64 overflow-auto bg-[#F9FAFB] p-2 rounded">{JSON.stringify(v.snapshot, null, 2)}</pre>
                </details>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BilingualBody({ lang, dir, title, value, onChange, preview, testId }) {
  return (
    <div className="hah-card p-3">
      <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-2">{title}</div>
      {preview ? (
        <div className="prose prose-sm max-w-none min-h-[300px] text-[#111827]" dir={dir}
             data-testid={`${testId}-preview`}>
          {value ? <ReactMarkdown>{value}</ReactMarkdown>
                 : <span className="text-[#9CA3AF]">Nothing to preview yet — write some Markdown.</span>}
        </div>
      ) : (
        <Textarea rows={16} value={value} dir={dir}
                  className="font-mono text-[13px] whitespace-pre-wrap"
                  placeholder={`Write ${lang.toUpperCase()} content in Markdown…\n\n# Heading\n\n**Bold** and *italic*.\n\n- Bullet one\n- Bullet two`}
                  onChange={(e) => onChange(e.target.value)}
                  data-testid={testId} />
      )}
    </div>
  );
}
